import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { createNewAsset } from '../../../actions/inventory';
import moment from 'moment';
import { actualValueForDropdown } from '../../../utils/common';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import Select from '../../../common/Form/Select';
import DatePicker from '../../../common/Form/DatePicker';
import Checkbox from '../../../common/Form/Checkbox';
import Customer from '../../../components/Inputs/Customer';
import Site from '../../../components/Inputs/Site';
import AssetType from '../../../components/Inputs/AssetType';
import RenderContact from '../../../components/Inputs/Contact';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';

export const warrantyType = [
    { key: 1, value: 'MANUFACTURING', label: 'Manufacturing' },
    { key: 2, value: 'INSTALLATION', label: 'Installation' },
    { key: 3, value: 'BILLING', label: 'Billing' },
];

const formName = 'addAsset';

export const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.4 } }
};

export const slideIn = {
    hidden: { x: -20, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.3 } }
};

export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const AddAsset = ({ location, location_id, serial_number, org_id, additionalProps, onClose = () => { }, asset_type_id, onSuccess = () => { } }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const defaultValues = { serial_number: serial_number ? serial_number : '', customer: org_id, hasSameOwner: true, location_id };
    const [formSubmitted, setFormSubmitted] = useState(false);

    const onCancel = () => {
        dispatch(closeModalAction());
        onClose();
    }

    const submit = (formValues) => {
        setFormSubmitted(true);
        let owner;
        let owner_id;

        if (formValues?.hasSameOwner && formValues?.location_id?.owners?.length > 0) {
            owner_id = formValues?.location_id?.owners[0]?.owner_id;
            owner = undefined;
        } else {
            if (actualValueForDropdown(formValues?.contact) === 'add-new-contact') {
                owner = {
                    name: formValues[`contact:name`],
                    email: formValues[`contact:email`],
                    mobile: formValues[`contact:mobile`]
                }
                owner_id = undefined;
            } else if (formValues?.contact) {
                owner_id = formValues.contact.id;
                owner = undefined;
            }
        }

        const payload = {
            asset_type_id: actualValueForDropdown(formValues.asset_type_id),
            location_id: actualValueForDropdown(formValues.location_id),
            org_id: actualValueForDropdown(formValues.customer),
            owner,
            owner_id,
            added_on: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            external_id: formValues.external_id,
            status: "AVAILABLE",
            working_condition: "NEW",
            is_temporary: false,
            is_spare: false,
            warranty_type: actualValueForDropdown(formValues.warranty_type),
            warranty_start_date: formValues.warranty_start_date,
            warranty_end_date: formValues.warranty_end_date,
        };

        let additional_fields = [];
        formValues.asset_type_id.fields.forEach(field => {
            if (formValues[field.key] && formValues[field.key] !== '') {
                additional_fields.push({
                    key: field.key,
                    value: formValues[field.key]
                });
            }
        });

        payload.additional_fields = additional_fields;

        let refreshAssetList = true;
        if (location || additionalProps && additionalProps.source === 'create_ticket_form') refreshAssetList = false;
        dispatch(createNewAsset({ payload, formName, onSuccess, refreshAssetList, additionalProps }));
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const selectedOrg = methods.watch('customer');
    const selectedAssetType = methods.watch('asset_type_id');
    const hasWarranty = methods.watch('hasWarranty');
    const hasSameOwner = methods.watch('hasSameOwner');
    const submitRef = useRef(null);

    return (
        <motion.div
            // initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="w-full h-full flex flex-col overflow-hidden max-h-[65vh]"
        >
            <div className='h-full flex-1 overflow-auto py-4'>
                <Form
                    className='flex-1 flex flex-col'
                    onSubmit={submit}
                    submitRef={submitRef}
                    methods={methods}
                >
                    <div className='flex-1 overflow-y-auto'>
                        {location && <SiteDetails location={location} />}

                        <motion.div
                            className='px-3 py-4 bg-white rounded-lg shadow-sm'
                            variants={staggerContainer}
                        >
                            <motion.div
                                className='grid grid-cols-4 gap-4'
                                variants={staggerContainer}
                            >
                                {!org_id && (
                                    <motion.div variants={slideIn}>
                                        <Customer name='customer' label='Customer' is_rma />
                                    </motion.div>
                                )}

                                {!location_id && <motion.div variants={slideIn}>
                                    <Site
                                        label='Site'
                                        required
                                        name='location_id'
                                        org_id={actualValueForDropdown(selectedOrg)}
                                        prefill={location_id ? [location_id] : []}
                                    />
                                </motion.div>
                                }

                                <motion.div variants={slideIn}>
                                    <Input
                                        label='External Id'
                                        name='external_id'
                                        type={'text'}
                                        className="w-full"
                                    />
                                </motion.div>

                                <motion.div variants={slideIn}>
                                    <AssetType
                                        addNew={false}
                                        label='Asset Type'
                                        required
                                        name='asset_type_id'
                                        org_id={actualValueForDropdown(selectedOrg)}
                                        prefill={asset_type_id ? [asset_type_id] : []}
                                    />
                                </motion.div>

                                {selectedAssetType && selectedAssetType.fields.map((field, index) => (
                                    <motion.div key={field.key} variants={slideIn}>
                                        <Input
                                            label={field.label}
                                            name={field.key}
                                            type={'text'}
                                            className="w-full"
                                            required={field.is_required}
                                        />
                                    </motion.div>
                                ))}
                            </motion.div>

                            <motion.div
                                className='mt-2 p-3 bg-gray-50 rounded-md'
                                variants={slideIn}
                            >
                                <Checkbox
                                    label='Owner same as Site'
                                    name='hasSameOwner'
                                    id='hasSameOwner'
                                />
                            </motion.div>

                            {!hasSameOwner && (
                                <motion.div
                                    className='mt-4'
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <RenderContact
                                        name={'contact'}
                                        label={'Contact Name'}
                                        org_id={actualValueForDropdown(selectedOrg)}
                                    />
                                </motion.div>
                            )}

                            <motion.div
                                className='mt-2 p-3 bg-gray-50 rounded-md'
                                variants={slideIn}
                            >
                                <Checkbox
                                    label='Do you have Warranty Details'
                                    name='hasWarranty'
                                    id='hasWarranty'
                                />
                            </motion.div>

                            {hasWarranty && (
                                <motion.div
                                    className='mt-4 flex gap-4'
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Select
                                        label='Type'
                                        type={'dropdown'}
                                        name='warranty_type'
                                        options={warrantyType}
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                    <DatePicker
                                        label='Warranty Start Date'
                                        name='warranty_start_date'
                                        dateFormat='dd-MM-yyyy'
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                    <DatePicker
                                        label='Warranty End Date'
                                        name='warranty_end_date'
                                        dateFormat='dd-MM-yyyy'
                                        shouldUnregister
                                        className="bg-white w-full"
                                    />
                                </motion.div>
                            )}
                        </motion.div>
                    </div>
                </Form>
            </div>
            <div className='px-4 flex justify-end py-4 gap-4 border-t bg-white z-10'>
                <ButtonScogoPrimary
                    textOrComponent={'Submit'}
                    loading={formButtonLoading[formName]}
                    onCancel={() => dispatch(closeModalAction())}
                    onClick={() => {
                        submitRef?.current?.click();
                    }}
                />
                <ButtonScogoClosedOutlined
                    onClick={onCancel}
                    textOrComponent={'Cancel'}
                />
            </div>
        </motion.div>
    );
};

export default AddAsset;


export const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 300, damping: 24 }
    }
};

export const SiteDetails = ({ location, label = 'Site', className = 'grid grid-cols-4 gap-3', padding = 'p-4 px-8 mb-2' }) => {
    if (!location) return null;

    return (
        <motion.div
            variants={itemVariants}
            className={`bg-blue-50 ${padding} border border-blue-100 shadow-sm`}
        >
            <motion.h3
                variants={itemVariants}
                className='text-blue-800 font-medium mb-2 flex items-center text-base'
            >
                <span className='material-icons mr-2 text-blue-600 text-sm'>location_on</span>
                {label} Information
            </motion.h3>
            <div className={className}>
                {location?.organization?.name && (
                    <motion.div
                        variants={itemVariants}
                        className='bg-white p-3 rounded-md shadow-sm'
                    >
                        <p className='text-xs text-gray-500 uppercase font-semibold'>Customer</p>
                        <p className='font-medium text-gray-800 text-sm truncate'>{location.organization.name}</p>
                    </motion.div>
                )}
                {location?.end_user?.name && (
                    <motion.div
                        variants={itemVariants}
                        className='bg-white p-3 rounded-md shadow-sm'
                    >
                        <p className='text-xs text-gray-500 uppercase font-semibold'>End User</p>
                        <p className='font-medium text-gray-800 text-sm truncate'>{location.end_user.name}</p>
                    </motion.div>
                )}
                {location?.external_id && (
                    <motion.div
                        variants={itemVariants}
                        className='bg-white p-3 rounded-md shadow-sm'
                    >
                        <p className='text-xs text-gray-500 uppercase font-semibold'>{label} ID</p>
                        <p className='font-medium text-gray-800 text-sm truncate'>{location.external_id}</p>
                    </motion.div>
                )}
                {location?.branch?.name && (
                    <motion.div
                        variants={itemVariants}
                        className='bg-white p-3 rounded-md shadow-sm'
                    >
                        <p className='text-xs text-gray-500 uppercase font-semibold'>Branch</p>
                        <p className='font-medium text-gray-800 text-sm truncate'>{location.branch.name}</p>
                    </motion.div>
                )}
                {location?.address && (
                    <motion.div
                        variants={itemVariants}
                        className='bg-white p-3 rounded-md shadow-sm'
                    >
                        <p className='text-xs text-gray-500 uppercase font-semibold'>Address</p>
                        <p className='text-gray-800 text-xs line-clamp-1'>{location.address}</p>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};
